import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine } from "./Reveal";
import { SevenPointStar, JordanFlag } from "./Star";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const starY = useTransform(scrollYProgress, [0, 1], [0, 260]);
  const starRotate = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-between overflow-hidden px-5 pt-32 sm:px-10 sm:pt-36"
    >
      <motion.div
        style={{ y: starY }}
        className="pointer-events-none absolute -right-24 top-24 z-0 sm:right-10 sm:top-16"
        aria-hidden="true"
      >
        <motion.div style={{ rotate: starRotate }} className="relative">
          <SevenPointStar className="h-64 w-64 text-jordan-red/15 sm:h-[30rem] sm:w-[30rem]" />
        </motion.div>
        <img
          src="/assets/logo.png"
          alt=""
          data-testid="hero-star-logo"
          className="absolute left-1/2 top-1/2 h-24 w-auto -translate-x-1/2 -translate-y-1/2 opacity-80 sm:h-44"
        />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10">
        <MaskedLine index={0}>
          <span className="mb-6 flex items-center gap-4 font-display text-xs font-bold uppercase tracking-[0.3em] text-jordan-red sm:text-sm">
            <JordanFlag className="h-4 w-8" data-testid="hero-jordan-flag" />
            Almadafah · Jordanian roots, Australian home
          </span>
        </MaskedLine>
        <h1 className="font-display font-black uppercase leading-[0.86] tracking-tighter text-jordan-ink">
          <MaskedLine index={1} className="text-[13vw] text-jordan-red sm:text-[12vw] lg:text-[11vw]">
            Jordanian
          </MaskedLine>
          <MaskedLine index={2} className="text-[13vw] sm:text-[12vw] lg:text-[11vw]">
            Hub<span className="text-jordan-green">.</span>
          </MaskedLine>
          <MaskedLine index={3}>
            <span className="mt-2 block font-arabic text-[11vw] leading-none text-jordan-green sm:mt-4 sm:text-[7.5vw] lg:text-[6.5vw]" data-testid="hero-arabic-word">
              المضافة
            </span>
          </MaskedLine>
        </h1>
      </motion.div>

      <div className="relative z-10 flex flex-col gap-10 pb-12 sm:flex-row sm:items-end sm:justify-between">
        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.9 }}
          className="max-w-md font-serifx text-xl italic leading-snug text-jordan-mute sm:text-2xl"
        >
          A community house for Jordanians in Australia — where culture is celebrated,
          hands are held, and no one eats alone.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="flex items-center gap-6"
        >
          <a
            href="#about"
            data-testid="hero-explore-btn"
            className="group flex items-center gap-3 font-display text-xs font-bold uppercase tracking-[0.25em] text-jordan-ink"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-jordan-ink transition-colors duration-300 group-hover:bg-jordan-ink group-hover:text-jordan-sand">
              ↓
            </span>
            Scroll the story
          </a>
        </motion.div>
      </div>
    </section>
  );
}
